import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@arcadeai/design-system/components/ui/atoms/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@arcadeai/design-system/components/ui/atoms/popover";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { cn } from "@arcadeai/design-system/lib/utils";
import type { Column } from "@tanstack/react-table";
import { Check, Filter } from "lucide-react";
import type * as React from "react";
import { memo } from "react";

export type DataTableFacetedFilterOption = {
  label: string;
  value: string;
  icon?: React.ElementType;
};

type DataTableFacetedFilterProps<TData, TValue> = {
  column?: Column<TData, TValue>;
  title?: string;
  options: DataTableFacetedFilterOption[];
  selectedValues: string[];
  onSelectionChange: (selectedValues: string[]) => void;
};

function DataTableFacetedFilterComponent<TData, TValue>({
  column,
  title,
  options,
  selectedValues,
  onSelectionChange,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const facets = column?.getFacetedUniqueValues();
  const selectedValuesSet = new Set(selectedValues);

  const handleSelectionChange = (value: string) => {
    const newSelectedValues = [...selectedValues];

    if (selectedValuesSet.has(value)) {
      // Remove the value if it's already selected
      const index = newSelectedValues.indexOf(value);
      if (index !== -1) {
        newSelectedValues.splice(index, 1);
      }
    } else {
      // Add the value if it's not selected
      newSelectedValues.push(value);
    }

    onSelectionChange(newSelectedValues);
  };

  const handleClearFilters = () => {
    onSelectionChange([]);
  };

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button className="h-8 border-dashed" size="sm" variant="outline" />
        }
      >
        <Filter className="mr-2 size-4" />
        {title}
        {selectedValuesSet.size > 0 && (
          <>
            <Separator className="mx-2 h-4" orientation="vertical" />
            <Badge
              className="rounded-sm px-1 font-normal lg:hidden"
              variant="secondary"
            >
              {selectedValuesSet.size}
            </Badge>
            <div className="hidden space-x-1 lg:flex">
              {selectedValuesSet.size > 2 ? (
                <Badge
                  className="rounded-sm px-1 font-normal"
                  variant="secondary"
                >
                  {selectedValuesSet.size} selected
                </Badge>
              ) : (
                selectedValues.map((value) => {
                  const selectedOption = options.find(
                    (opt) => opt.value === value
                  );
                  return (
                    <Badge
                      className="rounded-sm px-1 font-normal"
                      key={value}
                      variant="secondary"
                    >
                      {selectedOption ? selectedOption.label : value || ""}
                    </Badge>
                  );
                })
              )}
            </div>
          </>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValuesSet.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleSelectionChange(option.value)}
                  >
                    <div
                      className={cn(
                        "mr-2 flex size-4 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <Check className="size-4" />
                    </div>
                    {option.icon ? (
                      <div className="mr-1">
                        <option.icon className="size-4" />
                      </div>
                    ) : null}
                    <span>{option.label}</span>
                    {facets?.get(option.value) && (
                      <span className="ml-auto flex size-4 items-center justify-center font-mono text-xs">
                        {facets.get(option.value)}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValuesSet.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    className="justify-center text-center"
                    onSelect={handleClearFilters}
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
export const DataTableFacetedFilter = memo(
  DataTableFacetedFilterComponent
) as typeof DataTableFacetedFilterComponent;
